import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/**
 * Display abbreviations per IANA zone: [standard time, daylight saving time].
 * Offsets always come from the browser's timezone database; this table only names them.
 * Zones not listed here use the short name from Intl (e.g. "GMT+5").
 */
const TIMEZONE_ABBR: Record<string, [string, string?]> = {
  // North America
  'America/St_Johns': ['NST', 'NDT'],
  'America/Halifax': ['AST', 'ADT'],
  'America/New_York': ['EST', 'EDT'],
  'America/Chicago': ['CST', 'CDT'],
  'America/Denver': ['MST', 'MDT'],
  'America/Los_Angeles': ['PST', 'PDT'],
  'America/Anchorage': ['AKST', 'AKDT'],
  'America/Caracas': ['VET'],
  'America/Sao_Paulo': ['BRT'],
  'America/Argentina/Buenos_Aires': ['ART'],
  'America/Noronha': ['FNT'],

  // Pacific
  'Pacific/Auckland': ['NZST', 'NZDT'],
  'Pacific/Midway': ['SST'],
  'Pacific/Niue': ['NUT'],
  'Pacific/Honolulu': ['HST'],
  'Pacific/Marquesas': ['MART'],
  'Pacific/Gambier': ['GAMT'],
  'Pacific/Noumea': ['NCT'],
  'Pacific/Guadalcanal': ['SBT'],
  'Pacific/Fiji': ['FJT'],
  'Pacific/Tongatapu': ['TOT'],
  'Pacific/Kiritimati': ['LINT'],

  // Europe and Atlantic
  'Europe/London': ['GMT', 'BST'],
  'Europe/Berlin': ['CET', 'CEST'],
  'Europe/Paris': ['CET', 'CEST'],
  'Europe/Amsterdam': ['CET', 'CEST'],
  'Europe/Rome': ['CET', 'CEST'],
  'Europe/Madrid': ['CET', 'CEST'],
  'Europe/Bucharest': ['EET', 'EEST'],
  'Europe/Helsinki': ['EET', 'EEST'],
  'Europe/Athens': ['EET', 'EEST'],
  'Europe/Kiev': ['EET', 'EEST'],
  'Europe/Kyiv': ['EET', 'EEST'],
  'Europe/Moscow': ['MSK'],
  'Atlantic/Azores': ['AZOT', 'AZOST'],
  'Atlantic/South_Georgia': ['GST'],

  // Asia and Indian Ocean
  'Asia/Tehran': ['IRST'],
  'Asia/Dubai': ['GST'],
  'Asia/Kabul': ['AFT'],
  'Asia/Karachi': ['PKT'],
  'Asia/Kolkata': ['IST'],
  'Asia/Kathmandu': ['NPT'],
  'Asia/Dhaka': ['BST'],
  'Indian/Cocos': ['CCT'],
  'Asia/Bangkok': ['ICT'],
  'Asia/Shanghai': ['CST'],
  'Asia/Tokyo': ['JST'],

  // Australia
  'Australia/Eucla': ['ACWST'],
  'Australia/Darwin': ['ACST'],
  'Australia/Sydney': ['AEST', 'AEDT'],
  'Australia/Lord_Howe': ['LHST', 'LHDT'],

  // UTC and GMT
  UTC: ['UTC'],
  GMT: ['GMT'],
  'Etc/UTC': ['UTC'],
  'Etc/GMT': ['GMT'],
};

/**
 * Validates if a timezone identifier is a known IANA zone
 */
export function isValidTimezone(timezoneString: string): boolean {
  if (!timezoneString) return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezoneString });
    return true;
  } catch {
    return false;
  }
}

function intlShortName(timezoneString: string, date: Date): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: timezoneString, timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value || timezoneString;
}

/**
 * Gets the UTC offset (hours) and abbreviation of a timezone at a given moment,
 * so daylight saving time is taken into account. Unknown zones fall back to UTC.
 */
export function getTimezoneInfo(
  timezoneString: string,
  at: string | Date | dayjs.Dayjs = new Date(),
): { offset: number; abbr: string } {
  if (!isValidTimezone(timezoneString)) return { offset: 0, abbr: 'UTC' };

  const moment = dayjs.utc(at);
  const offsetMinutes = moment.tz(timezoneString).utcOffset();
  const names = TIMEZONE_ABBR[timezoneString];
  if (!names) {
    return { offset: offsetMinutes / 60, abbr: intlShortName(timezoneString, moment.toDate()) };
  }

  // A zone is on daylight saving time when its offset is above the year's standard (lowest) offset
  const year = moment.year();
  const standardOffset = Math.min(
    dayjs.utc(`${year}-01-01`).tz(timezoneString).utcOffset(),
    dayjs.utc(`${year}-07-01`).tz(timezoneString).utcOffset(),
  );
  const [standardAbbr, daylightAbbr] = names;
  const abbr = offsetMinutes > standardOffset && daylightAbbr ? daylightAbbr : standardAbbr;
  return { offset: offsetMinutes / 60, abbr };
}

/**
 * Convert a UTC datetime string to a specific timezone
 * Returns a dayjs object in the specified timezone
 */
export function fromJsonToTimezone(
  dateString: string | null,
  timezoneString: string | null,
): dayjs.Dayjs | null {
  if (!dateString || !timezoneString) return null;

  try {
    const utcDateTime = dayjs.utc(dateString);
    if (!isValidTimezone(timezoneString)) return utcDateTime;

    return utcDateTime.tz(timezoneString);
  } catch (e) {
    console.error('Timezone conversion failed:', {
      dateString,
      timezoneString,
      error: e,
    });
    // Fallback to UTC
    if (dateString) {
      try {
        return dayjs.utc(dateString);
      } catch {
        return null;
      }
    }
    return null;
  }
}

/**
 * Convert a datetime string in a specific timezone to local timezone
 */
export function fromJsonWithTimezone(
  dateString: string | null,
  timezoneString: string | null,
): dayjs.Dayjs | null {
  if (!dateString) return null;

  try {
    // Check if the date string already contains timezone information
    if (dateString.endsWith('Z')
        || dateString.includes('+')
        || (dateString.includes('T') && dateString.length > 19)) {
      // Date string already has timezone information, parse it directly
      return dayjs(dateString);
    }

    // If no timezone string provided, fallback to basic parsing
    if (!timezoneString) {
      return dayjs(dateString);
    }

    if (!isValidTimezone(timezoneString)) return dayjs(dateString);

    // The wall-clock time is in the given zone
    return dayjs.tz(dateString, timezoneString);
  } catch (e) {
    console.error('Timezone conversion failed:', {
      dateString,
      timezoneString,
      error: e,
    });
    // Fallback to parsing as local time
    return dayjs(dateString);
  }
}

/**
 * Get day suffix (1st, 2nd, 3rd, 4th, etc.)
 */
export function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Format a datetime with timezone to display format
 * Example: "Sep 24th 12:00 PM ADT"; outside the current year "Oct 25th, 2025 6:00 PM CEST"
 */
export function formatDateTimeWithTimezone(
  dateString: string | null,
  timezoneString: string | null,
  now: dayjs.Dayjs = dayjs(),
): string | null {
  const tzDateTime = fromJsonToTimezone(dateString, timezoneString);
  if (!tzDateTime) return null;

  const day = tzDateTime.date();
  const suffix = getDaySuffix(day);
  const monthName = tzDateTime.format('MMM');
  const time = tzDateTime.format('h:mm A');
  const year = tzDateTime.year() === now.year() ? '' : `, ${tzDateTime.year()}`;

  // Abbreviation for that moment (e.g. EET in winter, EEST in summer)
  const tzAbbr = getTimezoneInfo(timezoneString || '', tzDateTime).abbr;

  return `${monthName} ${day}${suffix}${year} ${time} ${tzAbbr}`;
}

/**
 * Format a date to display format
 * Example: "Sep 24th, 2024"
 */
export function formatDate(dateString: string | null): string | null {
  if (!dateString) return null;

  try {
    const date = dayjs(dateString);
    const day = date.date();
    const suffix = getDaySuffix(day);
    const monthName = date.format('MMM');
    const year = date.format('YYYY');

    return `${monthName} ${day}${suffix}, ${year}`;
  } catch (e) {
    console.error('Date formatting failed:', { dateString, error: e });
    return null;
  }
}

/**
 * Format a date to display time only
 * Example: "12:00 PM"
 */
export function formatTime(dateString: string | null): string | null {
  if (!dateString) return null;

  try {
    const date = dayjs(dateString);
    return date.format('h:mm A');
  } catch (e) {
    console.error('Time formatting failed:', { dateString, error: e });
    return null;
  }
}

/**
 * An event's date in the event's own timezone (the venue's local time), so every viewer sees the
 * same wall-clock time. Without a known timezone the browser's local time is used, unlabeled.
 */
function eventMoment(date: string | null | undefined, timezoneString?: string | null) {
  if (!date) return null;
  if (timezoneString && isValidTimezone(timezoneString)) {
    return { moment: dayjs.utc(date).tz(timezoneString), timezone: timezoneString };
  }
  return { moment: dayjs(date), timezone: null };
}

function eventTimezoneLabel(event: { moment: dayjs.Dayjs; timezone: string | null }): string {
  return event.timezone ? ` ${getTimezoneInfo(event.timezone, event.moment).abbr}` : '';
}

/** "Saturday July 31st • 11:28 AM EEST" */
export function formatEventDateTime(
  date: string | null | undefined,
  timezoneString?: string | null,
): string {
  const event = eventMoment(date, timezoneString);
  if (!event) return '';
  return `${event.moment.format('dddd MMMM Do • h:mm A')}${eventTimezoneLabel(event)}`;
}

/** "07/31/27" */
export function formatEventShortDate(
  date: string | null | undefined,
  timezoneString?: string | null,
): string {
  return eventMoment(date, timezoneString)?.moment.format('MM/DD/YY') ?? '';
}

/** "11:28 am EEST" */
export function formatEventShortTime(
  date: string | null | undefined,
  timezoneString?: string | null,
): string {
  const event = eventMoment(date, timezoneString);
  if (!event) return '';
  return `${event.moment.format('hh:mm a')}${eventTimezoneLabel(event)}`;
}

/** `pattern` (dayjs format) in the event's timezone, unlabeled: "JUL", "31", "July 31st, 2027" */
export function formatEventPattern(
  date: string | null | undefined,
  timezoneString: string | null | undefined,
  pattern: string,
): string {
  return eventMoment(date, timezoneString)?.moment.format(pattern) ?? '';
}

/** "11:28 AM EEST" (ticket cards) */
export function formatEventTime(
  date: string | null | undefined,
  timezoneString?: string | null,
): string {
  const event = eventMoment(date, timezoneString);
  if (!event) return '';
  return `${event.moment.format('hh:mm A')}${eventTimezoneLabel(event)}`;
}
